/**
 * Property detail · Rooms tab.
 *
 * Lists every room in the property with thumbnail, status, current tenant and
 * price. Click a row → V2 room detail. The leading checkbox toggles selection
 * without navigating; selecting ≥ 1 room reveals a sticky toolbar with
 * [Bulk edit overrides] and [Clear].
 *
 * Apartment grouping (COL-225): once a property has apartments, rooms are
 * grouped under their apartment, with a trailing "Unassigned" section for rooms
 * that still resolve at the property level. With no apartments the list renders
 * flat exactly as before. Reordering via the leading drag handle is scoped
 * WITHIN a section — dragging never silently re-parents a room (membership is
 * changed from the room editor or the Assign rooms dialog). The new sequence is
 * persisted to `rooms.sort_order` (grouped-contiguous) optimistically and rolls
 * back on error.
 *
 * The Bulk edit dialog writes the same lessor / rep / bank override to every
 * selected room — useful when an owner owns several rooms (typically the
 * rooms making up one apartment).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BedDouble,
  Building2,
  EyeOff,
  GripVertical,
  Home,
  Pencil,
  Plus,
  Replace,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import type { Apartment, Room } from "@/types/property";
import { extractImageUrls } from "@/lib/v2-images";
import { formatEUR } from "@/lib/v2-format";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { BulkEditRoomsDialog } from "@/components/admin/v2/BulkEditRoomsDialog";
import { RoomCreateSheet } from "@/components/admin/v2/RoomCreateSheet";
import { ReassignRoomDialog } from "@/components/admin/v2/tenant/ReassignRoomDialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  deriveApartmentOccupancy,
  usePropertyOccupiedRoomIds,
  useApartments,
  useReorderRooms,
} from "@/hooks/v2/useProperties";
import {
  usePropertyActiveOccupants,
  type ActiveOccupant,
} from "@/hooks/v2/useReassignBookings";
import { useToast } from "@/hooks/use-toast";

type Props = {
  propertyId: number;
  rooms: Room[];
  /** Live search query, owned by the page-level search bar. Filters rooms by
   *  title, floor, size and (for super admins) the active tenant's name /
   *  email. Empty string = show all. */
  search?: string;
  /** When set (e.g. a room result was clicked in the global search), that room
   *  is scrolled into view and briefly flashed so the operator can spot it. */
  highlightRoomId?: number | null;
  /**
   * Set when this tab is rendered inside a single apartment's detail page —
   * enables the "+ Room" empty-state CTA, pre-assigning new rooms to this
   * apartment. Left unset for the property-level Rooms tab, which has no
   * single apartment context to pre-fill.
   */
  apartmentId?: number | null;
};

// Custom dataTransfer MIME so this drag-reorder doesn't conflict with any
// other drag surface on the page (file upload zones, etc.). Pattern mirrors
// ImageUploader.tsx — only events that carry this exact type are treated
// as room reorders.
const REORDER_MIME = "application/x-coliviq-room-index";

type RoomGroup = {
  key: string;
  apartment: Apartment | null;
  rooms: Room[];
};

export function RoomsTab({
  propertyId,
  rooms,
  search = "",
  highlightRoomId = null,
  apartmentId = null,
}: Props) {
  const { t } = useLanguage();
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const reorderMut = useReorderRooms();
  const { data: apartments = [] } = useApartments(propertyId);
  const [createOpen, setCreateOpen] = useState(false);

  // Set of selected room ids. Stays a Set rather than an array so the
  // O(1) `.has` / `.add` / `.delete` membership ops keep the row render
  // fast even with hundreds of rooms.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  // COL-220: super admins can launch the reassign dialog directly from
  // a room row when that room has an active occupant. One occupant
  // lookup for the whole property, then row-level button decides whether
  // to render itself based on Map membership.
  const { data: occupants = new Map() } = usePropertyActiveOccupants(
    isSuperAdmin ? propertyId : null
  );
  // Occupancy badges follow the Room detail rule, which also counts tenants
  // in their notice period (the occupants map above does not).
  const { data: occupiedRoomIds = new Set<number>() } =
    usePropertyOccupiedRoomIds(propertyId);
  const [reassignFor, setReassignFor] = useState<{
    bookingId: number;
    tenantName: string;
    fromRoomId: number;
  } | null>(null);
  function openReassign(roomId: number, occupant: ActiveOccupant) {
    setReassignFor({
      bookingId: occupant.bookingId,
      tenantName:
        occupant.name?.trim() ||
        occupant.email?.trim() ||
        `Tenant #${occupant.bookingId}`,
      fromRoomId: roomId,
    });
  }

  // Drag state is scoped to a (group key, local index) pair so a reorder
  // stays within one section — a room can't be dragged across apartments.
  const [dragging, setDragging] = useState<{ key: string; idx: number } | null>(
    null
  );
  const [dropTarget, setDropTarget] = useState<{
    key: string;
    idx: number;
  } | null>(null);

  // Client-side search over rooms: title, floor, size and — for super admins,
  // who are the only ones with the occupant lookup — the active tenant's name
  // or email. An empty query is a no-op so the fast path stays untouched.
  const isFiltering = search.trim().length > 0;
  const visibleRooms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((r) => {
      const occ = occupants.get(r.id);
      return (
        r.title.toLowerCase().includes(q) ||
        (r.floor ?? "").toLowerCase().includes(q) ||
        (r.size_sqm != null && String(r.size_sqm).includes(q)) ||
        (occ?.name ?? "").toLowerCase().includes(q) ||
        (occ?.email ?? "").toLowerCase().includes(q)
      );
    });
  }, [rooms, search, occupants]);

  // Group rooms under their apartment (in apartment sort order), with anything
  // unassigned — or pointing at an apartment not in this property — collected
  // into a trailing "Unassigned" section so no room is ever dropped from view.
  const groups = useMemo<RoomGroup[]>(() => {
    const aptIds = new Set(apartments.map((a) => a.id));
    const byApt = new Map<number, Room[]>();
    const unassigned: Room[] = [];
    for (const r of visibleRooms) {
      if (r.apartment_id != null && aptIds.has(r.apartment_id)) {
        const arr = byApt.get(r.apartment_id) ?? [];
        arr.push(r);
        byApt.set(r.apartment_id, arr);
      } else {
        unassigned.push(r);
      }
    }
    const result: RoomGroup[] = [];
    for (const a of apartments) {
      const rs = byApt.get(a.id);
      if (rs && rs.length) {
        result.push({ key: `apt-${a.id}`, apartment: a, rooms: rs });
      }
    }
    if (unassigned.length) {
      result.push({ key: "unassigned", apartment: null, rooms: unassigned });
    }
    return result;
  }, [visibleRooms, apartments]);

  // Only decorate with section headers once there's a real apartment to show.
  // With no apartments the single "unassigned" group renders flat, identical
  // to the pre-COL-225 behaviour.
  const grouped = groups.some((g) => g.apartment);

  // Bring a deep-linked room into view and flash it (global-search "room →
  // parent apartment detail, room highlighted"). The flash clears after a
  // moment so it reads as a transient "here it is" cue, not a persistent state.
  const highlightRef = useRef<HTMLDivElement>(null);
  const [flashRoom, setFlashRoom] = useState(false);
  useEffect(() => {
    if (highlightRoomId == null) return;
    const el = highlightRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashRoom(true);
    const timer = setTimeout(() => setFlashRoom(false), 2500);
    return () => clearTimeout(timer);
    // Keyed on the room id alone: RoomsTab only mounts once its rooms have
    // loaded, so the ref is attached on this first run. Re-running on every
    // `groups` change would restart the flash when the occupants query
    // resolves; we only want it to (re-)flash when the deep-linked id changes.
  }, [highlightRoomId]);

  // Resolve the actual Room objects from the selection — needed by the
  // bulk-edit dialog so it can preserve each room's existing
  // lessor_override fields when merging in the edits.
  const selectedRooms = useMemo(
    () => rooms.filter((r) => selectedIds.has(r.id)),
    [rooms, selectedIds]
  );

  function toggle(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clear() {
    setSelectedIds(new Set());
  }

  function selectAll() {
    setSelectedIds(new Set(visibleRooms.map((r) => r.id)));
  }

  // Reorder within a single group, then rebuild the property-wide order by
  // concatenating every group in display order. Persisting this way makes
  // `sort_order` grouped-contiguous, matching exactly what the user sees.
  function commitGroupReorder(
    groupKey: string,
    fromIdx: number,
    toIdx: number
  ) {
    if (fromIdx === toIdx) return;
    const orderedRoomIds: number[] = [];
    for (const g of groups) {
      if (g.key !== groupKey) {
        for (const r of g.rooms) orderedRoomIds.push(r.id);
        continue;
      }
      const arr = [...g.rooms];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      for (const r of arr) orderedRoomIds.push(r.id);
    }
    reorderMut.mutate(
      { propertyId, orderedRoomIds },
      {
        onError: (err) =>
          toast({
            title: t(
              "admin.error.couldNotReorderRooms",
              "Could not reorder rooms"
            ),
            description:
              err instanceof Error
                ? err.message
                : t("admin.error.unknownError", "Unknown error"),
            variant: "destructive",
          }),
      }
    );
  }

  function onRowDragStart(key: string, index: number) {
    return (e: React.DragEvent<HTMLDivElement>) => {
      setDragging({ key, idx: index });
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(REORDER_MIME, `${key}:${index}`);
    };
  }

  function onRowDragOver(key: string, index: number) {
    return (e: React.DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes(REORDER_MIME)) return;
      // Only allow dropping inside the same section the drag started in.
      if (!dragging || dragging.key !== key) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dropTarget?.key !== key || dropTarget?.idx !== index) {
        setDropTarget({ key, idx: index });
      }
    };
  }

  function onRowDrop(key: string, targetIndex: number) {
    return (e: React.DragEvent<HTMLDivElement>) => {
      const raw = e.dataTransfer.getData(REORDER_MIME);
      if (raw === "") return;
      e.preventDefault();
      e.stopPropagation();
      setDragging(null);
      setDropTarget(null);
      const sep = raw.indexOf(":");
      const srcKey = raw.slice(0, sep);
      const srcIdx = Number(raw.slice(sep + 1));
      // Cross-section drops are ignored — membership changes happen elsewhere.
      if (srcKey !== key || Number.isNaN(srcIdx)) return;
      commitGroupReorder(key, srcIdx, targetIndex);
    };
  }

  function onRowDragEnd() {
    setDragging(null);
    setDropTarget(null);
  }

  if (rooms.length === 0) {
    return (
      <>
        <Card className="border-dashed">
          <EmptyState
            icon={BedDouble}
            title={
              apartmentId != null
                ? t(
                    "admin.field.noRoomsTitleApartment",
                    "No rooms in this apartment yet"
                  )
                : t("admin.field.noRoomsTitle", "No rooms in this property yet")
            }
            description={
              apartmentId != null
                ? t(
                    "admin.field.noRoomsDescApartment",
                    "Add the first room to get started."
                  )
                : t(
                    "admin.field.noRoomsDesc",
                    "Add rooms via the Add Property wizard, or use the legacy admin UI to add rooms one at a time."
                  )
            }
            actions={
              apartmentId != null ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  {t("admin.rooms.roomColumn", "Room")}
                </Button>
              ) : undefined
            }
            compact
          />
        </Card>
        {apartmentId != null && (
          <RoomCreateSheet
            open={createOpen}
            onOpenChange={setCreateOpen}
            propertyId={propertyId}
            apartmentId={apartmentId}
          />
        )}
      </>
    );
  }

  const allSelected =
    visibleRooms.length > 0 && visibleRooms.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-2">
      {isFiltering && (
        <p className="px-1 text-sm text-muted-foreground">
          {visibleRooms.length} {t("common.of", "of")} {rooms.length}{" "}
          {rooms.length === 1
            ? t("admin.section.room", "room")
            : t("admin.section.rooms", "rooms")}
        </p>
      )}

      {/* Sticky selection toolbar — only shown when there's a selection.
          Sits above the room list, sticky-positioned so it stays visible
          while the editor scrolls through a long room list deciding what
          else to add to the batch. */}
      {someSelected && (
        <div className="sticky top-12 z-20 flex items-center gap-3 rounded-lg border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-3 py-2 shadow-sm">
          <span className="text-sm font-medium">
            {selectedIds.size} {t("common.of", "of")} {visibleRooms.length}{" "}
            {visibleRooms.length === 1
              ? t("admin.rooms.roomNoun", "room")
              : t("admin.rooms.roomsNoun", "rooms")}{" "}
            {t("admin.rooms.selected", "selected")}
          </span>
          <div className="flex-1" />
          {!allSelected && (
            <Button type="button" variant="ghost" size="sm" onClick={selectAll}>
              {t("admin.rooms.selectAll", "Select all")}
            </Button>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={clear}>
            <X className="h-4 w-4 mr-1" />
            {t("common.clear", "Clear")}
          </Button>
          <Button type="button" size="sm" onClick={() => setBulkOpen(true)}>
            <Pencil className="h-4 w-4 mr-1.5" />
            {t("admin.rooms.bulkEdit", "Bulk edit")}
          </Button>
        </div>
      )}

      {groups.map((group) => (
        <div key={group.key} className="space-y-2">
          {grouped &&
            (group.apartment ? (
              <div className="flex items-center justify-between gap-2 px-1 pt-3 pb-0.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-semibold">
                    {group.apartment.label}
                  </span>
                  <Badge variant="secondary" className="text-[11px]">
                    {group.rooms.length}{" "}
                    {group.rooms.length === 1
                      ? t("admin.section.room", "room")
                      : t("admin.section.rooms", "rooms")}
                  </Badge>
                  {(() => {
                    // Derived from active bookings (single source of truth),
                    // not the manual apartment.rental_status field.
                    const visible = group.rooms.filter((r) => !r.is_hidden);
                    if (visible.length === 0) return null;
                    const occ = deriveApartmentOccupancy(
                      visible.filter((r) => occupiedRoomIds.has(Number(r.id)))
                        .length,
                      visible.length
                    );
                    return (
                      <Badge variant="outline" className="text-[11px]">
                        {occ === "vacant"
                          ? t("admin.status.vacant", "Vacant")
                          : occ === "partial"
                            ? t(
                                "admin.status.partiallyOccupied",
                                "Partially occupied"
                              )
                            : t("admin.field.occupied", "Occupied")}
                      </Badge>
                    );
                  })()}
                </div>
                <Link
                  to={`/admin/properties/${propertyId}/apartments/${group.apartment.id}`}
                  className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  {t("admin.apartment.open", "Open")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-1 pt-3 pb-0.5">
                <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-semibold">
                  {t("admin.rooms.unassigned", "Unassigned")}
                </span>
                <Badge variant="secondary" className="text-[11px]">
                  {group.rooms.length}
                </Badge>
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  {t(
                    "admin.rooms.unassignedHint",
                    "Not part of any apartment — resolves at property level."
                  )}
                </span>
              </div>
            ))}

          {group.rooms.map((r, idx) => {
            const cover = extractImageUrls(r.images)[0];
            const status = effectiveRoomStatus(
              r,
              occupiedRoomIds.has(Number(r.id))
            );
            const isSelected = selectedIds.has(r.id);
            const isBeingDragged =
              dragging?.key === group.key && dragging.idx === idx;
            const isDropTarget =
              dragging?.key === group.key &&
              dropTarget?.key === group.key &&
              dropTarget.idx === idx &&
              dragging.idx !== idx;
            const isHighlighted = r.id === highlightRoomId && flashRoom;
            return (
              <Card
                key={r.id}
                ref={r.id === highlightRoomId ? highlightRef : undefined}
                draggable={!isFiltering}
                onDragStart={onRowDragStart(group.key, idx)}
                onDragOver={onRowDragOver(group.key, idx)}
                onDrop={onRowDrop(group.key, idx)}
                onDragEnd={onRowDragEnd}
                className={`p-3 transition-all ${
                  isSelected ? "ring-2 ring-primary" : "hover:shadow-sm"
                } ${isBeingDragged ? "opacity-50" : ""} ${
                  isDropTarget ? "ring-2 ring-primary/60 -translate-y-0.5" : ""
                } ${
                  isHighlighted
                    ? "ring-2 ring-primary ring-offset-2 bg-primary/5"
                    : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Drag handle. Cursor changes to grab/grabbing so it reads as
                      reorderable even before the user starts dragging. Sits
                      before the checkbox so the row's leading column is the
                      reorder affordance — matches list-row patterns elsewhere. */}
                  <span
                    className={`shrink-0 ${
                      isFiltering
                        ? "text-muted-foreground/20 cursor-not-allowed"
                        : "text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing"
                    }`}
                    aria-label={
                      isFiltering
                        ? t(
                            "admin.aria.reorderDisabledWhileSearching",
                            "Clear the search to reorder rooms"
                          )
                        : t("admin.aria.dragToReorder", "Drag to reorder")
                    }
                    title={
                      isFiltering
                        ? t(
                            "admin.aria.reorderDisabledWhileSearching",
                            "Clear the search to reorder rooms"
                          )
                        : t("admin.aria.dragToReorder", "Drag to reorder")
                    }
                  >
                    <GripVertical className="h-4 w-4" />
                  </span>

                  {/* Checkbox sits OUTSIDE the Link so clicking it toggles
                      selection without navigating. The rest of the row is
                      wrapped in a Link below. */}
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggle(r.id)}
                    aria-label={`${t("admin.rooms.selectRoomAria", "Select")} ${r.title}`}
                  />

                  <Link
                    to={`/admin/properties/${propertyId}/rooms/${r.id}`}
                    className="flex-1 min-w-0 flex items-center gap-4 group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                    draggable={false}
                  >
                    <div className="h-14 w-20 rounded-md overflow-hidden bg-muted shrink-0">
                      {cover ? (
                        <img
                          src={cover}
                          alt={r.title}
                          loading="lazy"
                          draggable={false}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <BedDouble className="h-5 w-5 text-muted-foreground/40" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium leading-tight">{r.title}</h3>
                        {r.is_hidden && (
                          <Badge variant="outline" className="gap-1 text-xs">
                            <EyeOff className="h-3 w-3" />{" "}
                            {t("common.hidden", "Hidden")}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        {r.floor && (
                          <span>
                            {t("admin.apartments.floor", "Floor")} {r.floor}
                          </span>
                        )}
                        {r.size_sqm && <span>{r.size_sqm} m²</span>}
                        {r.available_from && (
                          <span>
                            {t("admin.rooms.availableFrom", "Available from")}{" "}
                            {r.available_from}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <StatusBadge status={status} />
                      <div className="text-right">
                        <div className="text-sm font-semibold tabular-nums">
                          {formatEUR(r.price) ?? "—"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t("admin.rooms.perMonth", "/month")}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>

                  {/* COL-220 entry point: super-admin-only Reassign action
                      sits OUTSIDE the Link so clicking it doesn't navigate
                      to room detail. Only renders for rooms with an active
                      occupant — no one to reassign on a vacant room. */}
                  {isSuperAdmin &&
                    (() => {
                      const occupant = occupants.get(r.id);
                      if (!occupant) return null;
                      return (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            openReassign(r.id, occupant);
                          }}
                          title={t(
                            "admin.room.reassignHint",
                            "Reassign this room’s tenant"
                          )}
                        >
                          <Replace className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline text-xs">
                            {t("admin.room.reassign", "Reassign")}
                          </span>
                        </Button>
                      );
                    })()}
                </div>
              </Card>
            );
          })}
        </div>
      ))}

      {isFiltering && visibleRooms.length === 0 && (
        <Card className="border-dashed">
          <EmptyState
            icon={BedDouble}
            title={t("admin.rooms.noMatches", "No rooms match your search")}
            description={t(
              "admin.rooms.noMatchesDesc",
              "Try a different room name, floor or tenant."
            )}
            compact
          />
        </Card>
      )}

      <BulkEditRoomsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        rooms={selectedRooms}
        onApplied={() => {
          setBulkOpen(false);
          clear();
        }}
      />

      {isSuperAdmin && reassignFor && (
        <ReassignRoomDialog
          open={reassignFor !== null}
          onOpenChange={(o) => {
            if (!o) setReassignFor(null);
          }}
          initialBookingId={reassignFor.bookingId}
          initialTenantName={reassignFor.tenantName}
          initialFromRoomId={reassignFor.fromRoomId}
          propertyId={propertyId}
        />
      )}
    </div>
  );
}

// Occupancy is derived from active bookings (single source of truth), not the
// stale room.status / room.available fields. Manual "maintenance" is the only
// status not represented by a booking, so honor it when there's no tenant.
function effectiveRoomStatus(
  r: Room,
  hasActiveBooking: boolean
): "occupied" | "vacant" | "maintenance" {
  if (hasActiveBooking) return "occupied";
  if (r.status === "maintenance") return "maintenance";
  return "vacant";
}

function StatusBadge({
  status,
}: {
  status: "occupied" | "vacant" | "maintenance";
}) {
  const { t } = useLanguage();
  if (status === "occupied") {
    return (
      <Badge variant="default">{t("admin.field.occupied", "Occupied")}</Badge>
    );
  }
  if (status === "maintenance") {
    return (
      <Badge variant="secondary">
        {t("admin.status.maintenance", "Maintenance")}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-950"
    >
      {t("admin.status.vacant", "Vacant")}
    </Badge>
  );
}
